import { displayOrDownloadUrlDocument, useDownloadDocument, useFindDocumentsSuspense } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { WebsocketChannels } from "@bbj-types/websockets/body";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useWebsocketHandler } from "@root/utils/websocket";
import { ErrorBoundary, TypoTooltip } from "@styleguide";
import { Suspense, useEffect, useState } from "react";
import { InfoCard, infoCardClasses } from "./styles";

type TDocumentBehaviour = "download" | "display";

interface IDrawerOfferBodyDocumentProps {
  documentIds: ObjectId[];
}

interface IDrawerOfferBodyDocumentContentProps {
  getDocuments: ReturnType<typeof useFindDocumentsSuspense>;
}

interface IDrawerOfferBodyDocumentItemProps {
  document: ReturnType<IDrawerOfferBodyDocumentContentProps["getDocuments"]>[number];
}

const DrawerOfferBodyDocumentItem = ({ document }: IDrawerOfferBodyDocumentItemProps) => {
  const [loading, setLoading] = useState<TDocumentBehaviour>();
  const [, getDocumentDownloadUrl] = useDownloadDocument();

  const handleDocumentBehaviour = (behaviour: TDocumentBehaviour) => async () => {
    setLoading(behaviour);
    const response = await getDocumentDownloadUrl(document._id);
    await displayOrDownloadUrlDocument(response.data, behaviour, document.name, document.mimeType);
    setLoading(undefined);
  };

  return (
    <Tooltip
      title="Votre document est en cours d’analyse antivirus. Veuillez patienter une trentaine de secondes avant de pouvoir effectuer une action dessus."
      disableHoverListener={document.uploaded}
      arrow
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TypoTooltip
          data-hj-suppress="true"
          variant="caption"
          noWrap
          color={!document.uploaded ? "disabled.main" : undefined}
          TooltipProps={{ disableHoverListener: !document.uploaded }}
        >
          {document.name}
        </TypoTooltip>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
          }}
        >
          <IconButton
            size="small"
            color="primary"
            onClick={handleDocumentBehaviour("display")}
            disabled={Boolean(loading) || !document.uploaded}
          >
            {loading === "display" ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="inherit" />}
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={handleDocumentBehaviour("download")}
            disabled={Boolean(loading) || !document.uploaded}
          >
            {loading === "download" ? <CircularProgress size={16} /> : <DownloadIcon fontSize="inherit" />}
          </IconButton>
        </Stack>
      </Stack>
    </Tooltip>
  );
};

const DrawerOfferBodyDocumentContent = ({ getDocuments }: IDrawerOfferBodyDocumentContentProps) => {
  const requestedDocuments = getDocuments();
  const [documents, setDocuments] = useState(requestedDocuments);

  useEffect(() => {
    setDocuments(requestedDocuments);
  }, [requestedDocuments]);

  useWebsocketHandler(WebsocketChannels.DOCUMENT, "document", (body) => {
    const document = body.message;
    setDocuments((currentDocuments) => {
      const index = documents.findIndex((currentDocument) => currentDocument._id === document._id);
      if (index !== -1) {
        const newDocuments = [...currentDocuments];
        newDocuments[index] = document;
        return newDocuments;
      }
      return currentDocuments;
    });
  });

  return (
    <>
      <Typography variant="subtitle2SemiBold">Documents liés</Typography>
      <Stack>
        {documents.map((document) => (
          <DrawerOfferBodyDocumentItem key={document._id} document={document} />
        ))}
      </Stack>
    </>
  );
};

export const DrawerOfferBodyDocument = ({ documentIds }: IDrawerOfferBodyDocumentProps) => {
  const getDocuments = useFindDocumentsSuspense(documentIds);

  return (
    <ErrorBoundary
      fallback={
        <Alert color="error" sx={{ width: 225 }}>
          <Typography variant="body1SemiBold">Oops !</Typography>
          <Typography variant="body1">Une erreur inattendue est survenue</Typography>
        </Alert>
      }
    >
      <InfoCard className={infoCardClasses.root}>
        <Suspense fallback={<CircularProgress size="24px" sx={{ mx: "auto" }} />}>
          <DrawerOfferBodyDocumentContent getDocuments={getDocuments} />
        </Suspense>
      </InfoCard>
    </ErrorBoundary>
  );
};
